import type { TFunction } from 'i18next';
import { highlighter, otherColor, shade } from '../palette';
import { Occurrence, Operator, Pattern } from './Pattern';

/** How an occurrence relates to the first entry (the prototype) of its family, see backend/api/analysis. */
export type Relation =
  | 'prototype'
  | 'exact'
  | 'transposition'
  | 'inversion'
  | 'retrograde'
  | 'augmentation'
  | 'diminution'
  | 'variant';

export type Role = 'theme' | 'countersubject' | 'sequence' | 'imitation' | 'motif';

export interface FamilyOccurrence extends Occurrence {
  endOffset: number;
  measure: number;
  endMeasure: number;
  relation: Relation;
  semitones: number | null;
  steps: number | null;
  tonal: boolean;
  mismatches: number;
  factor: string | null;
  startPitch: string | null;
  startPitchClass: string | null;
  startMidi: number | null;
}

export interface Transition {
  from: number;
  to: number;
  fromVoice: number;
  toVoice: number;
  /** Pitch-level change between the first notes of both entries. */
  semitones: number | null;
  /** Quarter notes between the end of one entry and the start of the next (negative: they overlap). */
  gap: number;
  relation: Relation;
}

export interface Distributions {
  byVoice: Record<string, number>;
  byRelation: Record<string, number>;
  bySemitones: Record<string, number>;
  byStartPitchClass: Record<string, number>;
  byMeasure: Record<string, number>;
}

/**
 * The figure the discovery names a motif or a sequence by (backend api/analysis/naming.py): taken from the subject's
 * head or tail, a figure of the Figurenlehre, or the contour class; the frontend words it (automaticFamilyName).
 */
export interface Figure {
  key: string;
  direction?: 'up' | 'down';
  /** The family (discovery label) a motif is taken from. */
  source?: string;
  inverted?: boolean;
  fast?: boolean;
  interval?: string;
  exclamatio?: boolean;
  shape?: string;
  rhythm?: 'dotted' | 'corta';
}

export interface Family {
  label: string;
  role: Role;
  figure?: Figure;
  /** Label of the theme this family sounds against (countersubjects). */
  accompanies: string | null;
  score: number;
  length: number;
  duration: number;
  prototype: FamilyOccurrence;
  notes: string[];
  /** Scale steps and semitones of the prototype relative to its first note. */
  contour: number[];
  semitoneContour: number[];
  rhythm: number[];
  occurrences: FamilyOccurrence[];
  transitions: Transition[];
  distributions: Distributions;
  sequences: { voiceNumber: number; occurrences: number[]; steps: number }[];
  imitations: { leader: number; follower: number; delay: number }[];
}

export interface Discovery {
  title: string;
  families: Family[];
  voices: { number: number; notes: number }[];
  coverage: { notes: number; total: number };
  algorithmVersion: number;
  seconds: number;
}

export const roleOrder: Role[] = ['theme', 'countersubject', 'imitation', 'sequence', 'motif'];

/**
 * The operator of the Suggestions panel that relates an entry to the first entry of its family (backend
 * discovery.RELATIONS); the first entry and exact repetitions are the pattern itself (no operator).
 */
const operatorByRelation: Partial<Record<string, Operator>> = {
  augmentation: Operator.AUGMENTATION,
  diminution: Operator.DIMINUTION,
  inversion: Operator.MIRROR_X,
  retrograde: Operator.MIRROR_Y,
  // Not discovered (yet); mapped should the backend ever label entries so.
  retrograde_inversion: Operator.MIRROR_XY,
  transposition: Operator.TRANSPOSITION,
  variant: Operator.DEVIATION
};

export const operatorOfRelation = (relation: string): Operator | undefined =>
  operatorByRelation[relation];

/** The operators the discovery searches families with (backend discovery.RELATIONS); others never relate entries. */
export const discoveredOperators: Operator[] = Object.values(operatorByRelation).filter(
  (operator) => operator !== Operator.MIRROR_XY
);

/** Whether an entry is highlighted while the operators are hidden (the pattern itself always is). */
export const isEntryShown = (entry: FamilyOccurrence, hiddenOperators: Operator[]) => {
  const operator = operatorOfRelation(entry.relation);
  return operator === undefined || !hiddenOperators.includes(operator);
};

/** A discovered family as a MelodyVis pattern: highlighted in the sheet, the timeline and the graph. */
export const familyToPattern = (
  family: Family,
  id: string,
  color: string,
  description: string,
  label: string = family.label,
  /** Entries related by these operators are not highlighted (Suggestions panel). */
  hiddenOperators: Operator[] = []
): Pattern => ({
  color,
  connectedToByOccurrences: [],
  connectedToByOperator: [],
  description,
  // Its occurrences are complete already: no operator search is started for it.
  hasBeenQueried: true,
  id,
  isLocked: false,
  isUserDefined: false,
  isVisible: true,
  label,
  length: family.length,
  occurrences: family.occurrences
    .filter((entry) => isEntryShown(entry, hiddenOperators))
    .map(({ firstOffset, lastOffset, voiceNumber }) => ({
      firstOffset,
      lastOffset,
      voiceNumber
    })),
  uniqueOccurrencesCount: family.occurrences.filter((entry) => isEntryShown(entry, hiddenOperators))
    .length,
  voiceNumber: family.prototype.voiceNumber
});

/**
 * Colours per role as in the manual analysis of Figure 2: the theme yellow, the first and second countersubject light
 * blue and pink, imitations (canon) orange, sequences red. A further family of a role takes a shade of its role's
 * colour, so similar roles look similar; motifs take teal and indigo, then grey (palette.ts).
 */
const roleColors: Record<Role, string[]> = {
  theme: [highlighter.subject],
  countersubject: [highlighter.firstCountersubject, highlighter.secondCountersubject],
  imitation: [highlighter.canon],
  sequence: [highlighter.sequence],
  motif: [highlighter.firstMotif, highlighter.secondMotif]
};

const rolePrefix: Record<Role, string> = {
  theme: 'T',
  countersubject: 'CS',
  imitation: 'I',
  sequence: 'S',
  motif: 'M'
};

export const sortedFamilies = (families: Family[]) =>
  [...families].sort(
    (first, second) =>
      roleOrder.indexOf(first.role) - roleOrder.indexOf(second.role) ||
      first.prototype.firstOffset - second.prototype.firstOffset
  );

/**
 * Short labels in the style of a manual analysis (paper, Figure 2): T for the theme, CS1/CS2 for countersubjects,
 * I/S/M for imitations, sequences and motifs. They never collide with the analyst's own labels A, B, C...
 */
export const familyLabels = (families: Family[]): Record<string, string> => {
  const counts: Partial<Record<Role, number>> = {};
  const totals: Partial<Record<Role, number>> = {};
  families.forEach((family) => (totals[family.role] = (totals[family.role] ?? 0) + 1));

  return Object.fromEntries(
    sortedFamilies(families).map((family) => {
      counts[family.role] = (counts[family.role] ?? 0) + 1;
      const needsNumber = family.role !== 'theme' || (totals.theme ?? 0) > 1;
      return [family.label, `${rolePrefix[family.role]}${needsNumber ? counts[family.role] : ''}`];
    })
  );
};

export const familyColors = (families: Family[]): Record<string, string> => {
  const counts: Partial<Record<Role, number>> = {};

  return Object.fromEntries(
    sortedFamilies(families).map((family) => {
      const index = counts[family.role] ?? 0;
      counts[family.role] = index + 1;
      const colors = roleColors[family.role];
      if (index < colors.length) return [family.label, colors[index]];
      // Motifs beyond the coloured ones are grey; further families of the other roles shade their role's colour.
      return [
        family.label,
        family.role === 'motif'
          ? otherColor
          : shade(colors[colors.length - 1], index - colors.length + 1)
      ];
    })
  );
};

export const familyPatternID = (family: Family) => `family-${family.label}`;

const directed = ['chromatic', 'scale', 'tirata', 'arpeggio', 'neighbour', 'leap', 'sequence'];

/** The name a family starts with: its figure (motifs, sequences) as analyses name it, otherwise its role. */
export const automaticFamilyName = (
  family: Family,
  labels: Record<string, string>,
  translate: TFunction
): string => {
  const figure = family.figure;
  if (!figure || (figure.key === 'sequence' && !figure.direction))
    return translate(`discovery.roles.${family.role}`);
  const variant = figure.key === 'scale' && figure.fast ? 'tirata' : figure.key;
  const key =
    variant === 'contour'
      ? `contour_${figure.shape ?? 'level'}`
      : figure.direction && directed.includes(variant)
        ? `${variant}_${figure.direction}`
        : variant;
  let name = translate(`discovery.figures.${key}`, {
    interval: figure.interval
      ? translate(
          `discovery.${variant === 'sequence' ? 'sequenceIntervals' : 'leapIntervals'}.${figure.interval}`
        )
      : '',
    source: figure.source ? (labels[figure.source] ?? figure.source) : ''
  });
  if (figure.inverted) name = translate('discovery.figures.inverted', { name });
  if (figure.exclamatio) name = translate('discovery.figures.exclamatio', { name });
  if (figure.rhythm) name = translate(`discovery.figures.rhythm_${figure.rhythm}`, { name });
  return name;
};

/** The name the analyst gave a family (by its discovery label), otherwise the one it starts with. */
export const familyName = (
  family: Family,
  labels: Record<string, string>,
  translate: TFunction,
  names: Record<string, string> = {}
): string => names[family.label]?.trim() || automaticFamilyName(family, labels, translate);

const overlaps = (first: Occurrence, second: Occurrence) =>
  first.firstOffset <= second.lastOffset && second.firstOffset <= first.lastOffset;

/**
 * The graph's edges between shown families (the discovery itself has none, so the graph showed bare nodes): two
 * families are connected by the entries of the first that share notes with an entry of the second in the same voice
 * (a motif inside the theme), and a countersubject by the entries that sound against an entry of its theme.
 */
export const familyConnections = (families: Family[], shownIDs: string[]) => {
  const shown = families.filter((family) => shownIDs.includes(familyPatternID(family)));
  const connections = new Map<string, Pattern['connectedToByOccurrences']>(
    shown.map((family) => [familyPatternID(family), []])
  );

  shown.forEach((family, index) =>
    shown.slice(index + 1).forEach((other) => {
      const accompanies = family.accompanies === other.label || other.accompanies === family.label;
      const shared = family.occurrences.filter((occurrence) =>
        other.occurrences.some(
          (candidate) =>
            overlaps(occurrence, candidate) &&
            (accompanies || candidate.voiceNumber === occurrence.voiceNumber)
        )
      );
      if (shared.length === 0) return;

      const occurrences = shared.map(({ firstOffset, lastOffset, voiceNumber }) => ({
        firstOffset,
        lastOffset,
        voiceNumber
      }));
      connections.get(familyPatternID(family)).push({ id: familyPatternID(other), occurrences });
      connections.get(familyPatternID(other)).push({ id: familyPatternID(family), occurrences });
    })
  );

  return connections;
};

/**
 * For every family, the labels of the user's own patterns (selected in the sheet) that overlap one of its entries:
 * same voice, sharing at least half of the shorter of the two (paper 7.2, suggestions based on previous selections).
 */
export const ownPatternsByFamily = (
  families: Family[],
  patterns: Pattern[]
): Record<string, string[]> => {
  const own = patterns.filter((pattern) => pattern.isUserDefined);
  const overlaps = (entry: FamilyOccurrence, occurrence: Occurrence) => {
    if (entry.voiceNumber !== occurrence.voiceNumber) return false;
    const shared =
      Math.min(entry.lastOffset, occurrence.lastOffset) -
      Math.max(entry.firstOffset, occurrence.firstOffset);
    const shorter = Math.min(
      entry.lastOffset - entry.firstOffset,
      occurrence.lastOffset - occurrence.firstOffset
    );
    return shared >= 0 && shared >= shorter / 2;
  };

  return Object.fromEntries(
    families.map((family) => [
      family.label,
      own
        .filter((pattern) =>
          pattern.occurrences.some((occurrence) =>
            family.occurrences.some((entry) => overlaps(entry, occurrence))
          )
        )
        .map((pattern) => pattern.label)
    ])
  );
};
